import { useEffect, useRef, useState } from 'react';
import Avatar from '@/components/base/Avatar';
import type { WorkingCreators } from './workingCreators';


const VISIBLE = 5;
const SWAP_EVERY_MS = 3200;

/**
 * An overlapping stack of real creators' profile photos that keeps changing: every few seconds one
 * face pops out for another from the pool. Shows nothing when no creator has a photo.
 */
export default function CreatorAvatars({ creators, ring }: { creators: WorkingCreators; ring: string }) {
  const pool = creators.avatars.filter((creator) => creator.image);
  const [shown, setShown] = useState(() => pool.slice(0, VISIBLE).map((_, index) => index));
  const [swapped, setSwapped] = useState(-1);
  const shownRef = useRef(shown);
  shownRef.current = shown;
  const nextRef = useRef(VISIBLE);

  useEffect(() => {
    setShown(pool.slice(0, VISIBLE).map((_, index) => index));
    // Only when the pool itself changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pool.length]);

  useEffect(() => {
    if (pool.length <= VISIBLE || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    const timer = window.setInterval(() => {
      // Bring in the next creator not on show, in a random slot (the pool is bigger than the stack).
      const current = shownRef.current;
      let candidate = nextRef.current;
      while (current.includes(candidate % pool.length)) candidate += 1;
      nextRef.current = candidate + 1;
      const slot = Math.floor(Math.random() * current.length);
      const updated = [...current];
      updated[slot] = candidate % pool.length;
      setShown(updated);
      setSwapped(slot);
    }, SWAP_EVERY_MS);
    return () => window.clearInterval(timer);
  }, [pool.length]);

  if (pool.length === 0) return null;
  const more = creators.total - shown.length;
  return (
    <div className="flex -space-x-2.5">
      {shown.map((poolIndex, slot) => {
        const creator = pool[poolIndex];
        return (
          <span key={`${slot}-${poolIndex}`} className={slot === swapped ? 'join-avatar-in' : ''}>
            <Avatar src={creator.image} alt={creator.name} size="md" className={`border-2 ${ring}`} />
          </span>
        );
      })}
      {more > 0 ? (
        <span
          className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-2 bg-primary-700 text-xs font-semibold text-background-50 ${ring}`}
        >
          +{more}
        </span>
      ) : null}
    </div>
  );
}
